'use client';

import { useState, useCallback, useContext } from 'react';
import type {
  ChatMessage,
  BugReport,
  ErrorBoundaryEvent,
  NetworkRequestEntry,
  EnvironmentInfo,
  AppStateSnapshot,
  ConsoleLogEntry,
  DebugChatAction,
} from '../context/types';
import { RuntimeContext } from '../context/RuntimeContextProvider';
import { sanitizeContext } from '../lib/sanitizer';
import { getDebugChatApiPath } from './debugChatApiPath';

// ============================================================================
// Pure logic helpers (exported for unit testing in Node env)
// ============================================================================

/** Generate a simple unique ID (no crypto dependency for SSR compatibility) */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function buildUserMessage(text: string): ChatMessage {
  return {
    id: generateId(),
    role: 'user',
    content: text,
    timestamp: Date.now(),
  };
}

export function buildAssistantMessage(
  text: string,
  metadata: ChatMessage['metadata'],
): ChatMessage {
  return {
    id: generateId(),
    role: 'assistant',
    content: text,
    timestamp: Date.now(),
    metadata,
  };
}

/**
 * Mock AI response that mentions error/failure counts and asks what went wrong.
 */
export function buildMockAiResponse(
  errorCount: number,
  failureCount: number,
): string {
  const parts: string[] = [];

  parts.push(
    `I can see ${errorCount} console error${errorCount !== 1 ? 's' : ''} and ${failureCount} network failure${failureCount !== 1 ? 's' : ''} in your session.`,
  );

  if (errorCount > 0 || failureCount > 0) {
    parts.push(
      `That's a good starting point. Can you describe what went wrong from your perspective?`,
    );
  } else {
    parts.push(
      `No obvious errors detected. Can you tell me more about the unexpected behavior you observed?`,
    );
  }

  parts.push(
    `You can also click **"Bug Report"** to auto-generate a structured report, or **"Agent Prompt"** to get a prompt you can paste into an AI agent.`,
  );

  return parts.join(' ');
}

/** Construct a mock ErrorBoundaryEvent */
function mockError(index: number): ErrorBoundaryEvent {
  return {
    timestamp: Date.now() - index * 1000,
    message: `Uncaught TypeError: Cannot read properties of undefined (reading 'balance') [mock #${index + 1}]`,
    stack: `TypeError: Cannot read properties of undefined (reading 'balance')\n    at WithdrawalForm (WithdrawalForm.tsx:42:18)`,
    componentStack: `\n    at WithdrawalForm\n    at AppDialogs`,
  };
}

/** Construct a mock NetworkRequestEntry that failed */
function mockNetworkFailure(index: number): NetworkRequestEntry {
  return {
    timestamp: Date.now() - index * 2000,
    method: 'POST',
    url: `https://api.ar-ca.org/v1/engine/${index % 2 === 0 ? 'execute_slow_mode' : 'get_subaccount_summary'}`,
    status: index % 2 === 0 ? 503 : null,
    durationMs: index % 2 === 0 ? 5023 : 0,
    error: index % 2 !== 0 ? 'NetworkError: Failed to fetch' : undefined,
    requestBodyPreview: '{"sender":"0x1234...abcd","txs":[{"type":"withdraw_collateral",...}]}',
    responseBodyPreview: index % 2 === 0 ? '{"error":"Service Unavailable"}' : undefined,
  };
}

function mockEnvironment(): EnvironmentInfo {
  return {
    userAgent:
      'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0.0.0 Safari/537.36',
    buildId: '0.3.2',
    dataEnv: 'testnet',
    nextPublicApiUrl: 'https://api.ar-ca.org',
    sentryEnabled: false,
    timestamp: Date.now(),
  };
}

function mockAppState(): AppStateSnapshot {
  return {
    wsConnectionStatus: 'connected',
    currentRoute: '/trading/BTC-PERP',
    walletConnected: true,
    walletAddress: '0x1234...abcd',
    selectedMarket: 'BTC-PERP',
    visibleDialogs: [],
    screenSize: { width: 1440, height: 900 },
    timestamp: Date.now(),
  };
}

/**
 * Generate a mock BugReport with the given number of captured errors and
 * network failures (for testing shape compliance).
 */
export function generateMockBugReport(
  errorCount: number,
  failureCount: number,
): BugReport {
  const capturedErrors: ErrorBoundaryEvent[] = Array.from(
    { length: errorCount },
    (_, i) => mockError(i),
  );

  const capturedNetworkFailures: NetworkRequestEntry[] = Array.from(
    { length: failureCount },
    (_, i) => mockNetworkFailure(i),
  );

  const relevantLogs: ConsoleLogEntry[] = [
    {
      timestamp: Date.now() - 5000,
      level: 'warn',
      args: ['[WithdrawalForm] subaccount summary returned empty balances'],
      source: 'WithdrawalForm',
    },
  ];

  return {
    title: 'Withdrawal form crashes when subaccount summary is empty',
    severity: 'major',
    description:
      'When the subaccount summary API returns an empty balances array, ' +
      'the withdrawal form throws an unhandled TypeError and unmounts.',
    reproductionSteps: [
      'Connect wallet on testnet',
      'Navigate to the withdrawal dialog',
      'Wait for subaccount summary to load',
      'Observe the error boundary rendering instead of the form',
    ],
    expectedBehavior: 'The withdrawal form renders with zero balances shown.',
    actualBehavior:
      'Error boundary is triggered: "Cannot read properties of undefined (reading \'balance\')".',
    capturedErrors,
    capturedNetworkFailures,
    environment: mockEnvironment(),
    appStateAtTime: mockAppState(),
    relevantLogs,
    affectedFiles: [
      'packages/stackd-perps/client/modules/collateral/withdraw/components/WithdrawalForm.tsx',
    ],
  };
}

/**
 * Generate a mock agent prompt string that an AI coding agent could act on.
 */
export function generateMockAgentPrompt(
  errorCount: number,
  failureCount: number,
): string {
  return `You are a debugging agent for a Next.js perpetuals trading app (ArCa Perps).

## Session Context
- Console errors: ${errorCount}
- Network failures: ${failureCount}
- Route: /trading/BTC-PERP
- Wallet: connected (testnet)
- Build: v0.3.2

## Observed Problem
The withdrawal form throws an unhandled TypeError when the subaccount summary
API returns an empty balances array. The error boundary catches it, but the root
cause is an unsafe property access at WithdrawalForm.tsx:42.

## Key Error
\`\`\`
TypeError: Cannot read properties of undefined (reading 'balance')
    at WithdrawalForm (WithdrawalForm.tsx:42:18)
\`\`\`

## Relevant Files
- packages/stackd-perps/client/modules/collateral/withdraw/components/WithdrawalForm.tsx
- packages/stackd-perps/client/modules/collateral/withdraw/hooks/useWithdrawalForm.ts

## Your Task
1. Reproduce the crash by writing a failing unit test for the WithdrawalForm
   component when passed an empty balances array.
2. Fix the unsafe property access with a safe default or early return.
3. Verify the test passes and no regressions are introduced.
4. Follow TDD: write the failing test FIRST, then implement the fix.

## Constraints
- Do NOT change existing tests to make them pass.
- Use the project's existing test infrastructure (Jest + ts-jest).
- Minimal fix — do not refactor unrelated code.`;
}

// ============================================================================
// API call helpers
// ============================================================================

/**
 * Read a plain-text streaming response body and call onChunk with each piece.
 * Returns the fully assembled text when the stream ends.
 */
async function readTextStream(
  response: Response,
  onChunk: (chunk: string) => void,
): Promise<string> {
  const reader = response.body?.getReader();
  if (!reader) return '';

  const decoder = new TextDecoder();
  let fullText = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    const chunk = decoder.decode(value, { stream: true });
    fullText += chunk;
    onChunk(fullText);
  }

  return fullText;
}

/**
 * POST to the Debug Chat API and stream the response text.
 * Calls onChunk with the accumulated text as it arrives.
 * Returns the full final text.
 *
 * Throws on non-2xx responses.
 */
async function callDebugChatApi(
  messages: Array<{ role: 'user' | 'assistant'; content: string }>,
  context: ReturnType<typeof sanitizeContext>,
  action: DebugChatAction,
  onChunk: (accumulated: string) => void,
): Promise<string> {
  const response = await fetch(getDebugChatApiPath(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ messages, context, action }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => 'Unknown error');
    throw new Error(`API error ${response.status}: ${text}`);
  }

  return readTextStream(response, onChunk);
}

// ============================================================================
// React hook
// ============================================================================

export interface UseDebugChatResult {
  messages: ChatMessage[];
  isLoading: boolean;
  sendMessage: (text: string) => void;
  generateBugReport: () => void;
  generateAgentPrompt: () => void;
  clearMessages: () => void;
}

// Mock counts used as fallback when API is unavailable
const MOCK_ERROR_COUNT = 2;
const MOCK_FAILURE_COUNT = 1;

export function useDebugChat(): UseDebugChatResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Grab the runtime context collector if available (may be null outside provider)
  const runtimeCtx = useContext(RuntimeContext);

  const appendMessage = useCallback((msg: ChatMessage) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  /** Update the content of the last assistant message in place (for streaming). */
  const updateLastAssistantMessage = useCallback((content: string) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (!last || last.role !== 'assistant') return prev;
      return [...prev.slice(0, -1), { ...last, content }];
    });
  }, []);

  /**
   * Convert current ChatMessage[] to the wire format expected by the API.
   * Excludes system messages and strips metadata.
   */
  const toWireMessages = useCallback(
    (msgs: ChatMessage[]): Array<{ role: 'user' | 'assistant'; content: string }> =>
      msgs
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content })),
    [],
  );

  const sendMessage = useCallback(
    (text: string) => {
      if (!text.trim()) return;

      const userMsg = buildUserMessage(text.trim());
      // Optimistically add user message and a placeholder assistant message
      const placeholderAssistant = buildAssistantMessage('', {});

      setMessages((prev) => {
        const withUser = [...prev, userMsg];
        const wireMessages = withUser
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

        // Fire API call as a side effect from within the state update callback
        // (not ideal, but avoids stale closure over messages)
        const context = runtimeCtx
          ? sanitizeContext(runtimeCtx.getContext())
          : sanitizeContext({
              consoleLogs: [],
              networkRequests: [],
              wsEvents: [],
              errors: [],
              performance: [],
              appState: {
                wsConnectionStatus: 'unknown',
                currentRoute: typeof window !== 'undefined' ? window.location.pathname : '/',
                walletConnected: false,
                visibleDialogs: [],
                screenSize: {
                  width: typeof window !== 'undefined' ? window.innerWidth : 0,
                  height: typeof window !== 'undefined' ? window.innerHeight : 0,
                },
                timestamp: Date.now(),
              },
              environment: {
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
                buildId: 'unknown',
                dataEnv: 'unknown',
                nextPublicApiUrl: '',
                sentryEnabled: false,
                timestamp: Date.now(),
              },
            });

        // Start the async API call outside the setState
        setTimeout(() => {
          setIsLoading(true);
          callDebugChatApi(wireMessages, context, 'chat', (accumulated) => {
            updateLastAssistantMessage(accumulated);
          })
            .then(() => {
              setIsLoading(false);
            })
            .catch(() => {
              // Graceful degradation: fall back to mock response
              const errorCount = context.errors.length;
              const failureCount = context.networkRequests.filter(
                (r) => r.status === null || r.status >= 400,
              ).length;
              updateLastAssistantMessage(buildMockAiResponse(errorCount, failureCount));
              setIsLoading(false);
            });
        }, 0);

        return [...withUser, placeholderAssistant];
      });
    },
    [runtimeCtx, updateLastAssistantMessage],
  );

  const generateBugReport = useCallback(() => {
    setIsLoading(true);

    const context = runtimeCtx
      ? sanitizeContext(runtimeCtx.getContext())
      : null;

    if (!context) {
      // No context available — fall back to mock
      setTimeout(() => {
        const report = generateMockBugReport(MOCK_ERROR_COUNT, MOCK_FAILURE_COUNT);
        appendMessage(
          buildAssistantMessage(
            `I've generated a bug report based on your session data. You can review it below and submit it to GitHub.`,
            { bugReport: report },
          ),
        );
        setIsLoading(false);
      }, 800);
      return;
    }

    const placeholderAssistant = buildAssistantMessage('Generating bug report…', {});
    appendMessage(placeholderAssistant);

    const wireMessages = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

    callDebugChatApi(wireMessages, context, 'generate_report', (accumulated) => {
      updateLastAssistantMessage(accumulated);
    })
      .then((fullText) => {
        // Try to parse the JSON bug report from the response
        try {
          const report: BugReport = JSON.parse(fullText);
          setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (!last || last.role !== 'assistant') return prev;
            return [
              ...prev.slice(0, -1),
              {
                ...last,
                content: `I've generated a bug report based on your session data. You can review it below and submit it to GitHub.`,
                metadata: { bugReport: report },
              },
            ];
          });
        } catch {
          // Model didn't return valid JSON — show raw text
        }
        setIsLoading(false);
      })
      .catch(() => {
        // Graceful degradation
        const errorCount = context.errors.length;
        const failureCount = context.networkRequests.filter(
          (r) => r.status === null || r.status >= 400,
        ).length;
        const report = generateMockBugReport(errorCount, failureCount);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (!last || last.role !== 'assistant') return prev;
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              content: `I've generated a bug report based on your session data. You can review it below and submit it to GitHub.`,
              metadata: { bugReport: report },
            },
          ];
        });
        setIsLoading(false);
      });
  }, [runtimeCtx, messages, appendMessage, updateLastAssistantMessage]);

  const generateAgentPrompt = useCallback(() => {
    setIsLoading(true);

    const context = runtimeCtx
      ? sanitizeContext(runtimeCtx.getContext())
      : null;

    if (!context) {
      setTimeout(() => {
        const prompt = generateMockAgentPrompt(MOCK_ERROR_COUNT, MOCK_FAILURE_COUNT);
        appendMessage(
          buildAssistantMessage(
            `Here's an agent prompt you can paste into Claude Code or another AI agent to fix this issue:`,
            { agentPrompt: prompt },
          ),
        );
        setIsLoading(false);
      }, 600);
      return;
    }

    const placeholderAssistant = buildAssistantMessage('Generating agent prompt…', {});
    appendMessage(placeholderAssistant);

    const wireMessages = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

    callDebugChatApi(wireMessages, context, 'generate_agent_prompt', (accumulated) => {
      updateLastAssistantMessage(accumulated);
    })
      .then((fullText) => {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (!last || last.role !== 'assistant') return prev;
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              content: `Here's an agent prompt you can paste into Claude Code or another AI agent to fix this issue:`,
              metadata: { agentPrompt: fullText },
            },
          ];
        });
        setIsLoading(false);
      })
      .catch(() => {
        const errorCount = context.errors.length;
        const failureCount = context.networkRequests.filter(
          (r) => r.status === null || r.status >= 400,
        ).length;
        const prompt = generateMockAgentPrompt(errorCount, failureCount);
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (!last || last.role !== 'assistant') return prev;
          return [
            ...prev.slice(0, -1),
            {
              ...last,
              content: `Here's an agent prompt you can paste into Claude Code or another AI agent to fix this issue:`,
              metadata: { agentPrompt: prompt },
            },
          ];
        });
        setIsLoading(false);
      });
  }, [runtimeCtx, messages, appendMessage, updateLastAssistantMessage]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  return {
    messages,
    isLoading,
    sendMessage,
    generateBugReport,
    generateAgentPrompt,
    clearMessages,
  };
}
